import PropTypes from 'prop-types';
import React from 'react';
import bindAll from 'lodash.bindall';
import {injectIntl, intlShape} from 'react-intl';
import VM from 'scratch-vm';

import Modal from '../../containers/modal.jsx';
import extensionData from '../../lib/libraries/extensions/index.jsx';
import {ROBOT_IDS, getGroup, setGroup, normGroup, groupChecked, markGroupChecked, GROUP_EVENT,
    isZeroGroup, noGroupChosen, setNoGroupChosen} from '../../lib/robot-connect.js';
import styles from '../group-number-modal/group-number-modal.css';
import rcStyles from './robot-connect.css';
// 실제 큐브 사진(2026-10-06 직원 의견 "캐릭터 말고 실제 블록 모양"). 원본: D:/Dev/artibo/artibo-python/icons → 128px
import motorPhoto from './img/motor.png';
import ledPhoto from './img/led.png';
import masterPhoto from './img/master.png';
import soundPhoto from './img/sound.png';
import proximityPhoto from './img/proximity.png';
import lighttouchPhoto from './img/lighttouch.png';

/**
 * 블록 연결(2026-10-01, 10-06 직원 의견으로 문구·사진 손봄) — 큐브로이드 로봇 모듈 연결을 한 창에서.
 *   화면 문구는 안내 말투(~합니다/~하세요), 긴 줄표 없이, 꼭 필요한 멘트만. 모듈은 실제 큐브 사진.
 *   ① 맨 위: 그룹 번호(없거나, 이번 수업에서 아직 확인 안 했으면 연결 버튼을 막고 먼저 묻는다)
 *   ② 아래: 모듈 7종 목록. 버튼은 늘 [연결하기](안 넣은 모듈은 블록 목록에 넣고 바로 연결). 상태(연결됨/찾는 중/못 찾음)가 한눈에.
 * [연결]을 누르면 그 클릭으로 크롬 페어링 창이 뜨고(크롬은 사람 클릭이 있어야 창을 띄운다), 고르면 자동으로 붙는다.
 * 설계·되돌리기: src/lib/robot-connect.js 머리말.
 */

const UI = {
    // 2026-10-06 직원 의견: 어린아이에게 말하는 말투(~해요) 대신 안내 문구(~합니다/~하세요), 긴 줄표 쓰지 않기, 멘트는 꼭 필요한 것만
    ko: {
        title: '블록 연결하기',
        groupLabel: '① 그룹 번호',
        groupHint: '블록에 붙은 그룹 번호를 입력해 주세요. 주변의 블루투스 혼선을 막고 내 블록만 안전하게 연결할 수 있습니다.',
        groupNeed: '그룹 번호를 먼저 입력해 주세요.',
        groupStale: n => `이전에 사용한 번호(${n})입니다. 지금 사용하는 블록 번호가 맞는지 확인해 주세요.`,
        groupYes: '맞습니다',
        groupSaved: '저장됨',
        // 2026-10-16: 자리표시 "00"이 들어 있는 값처럼 보였다(직원 캡처) → 예시로
        groupPh: '예: 12',
        noGroup: '한 세트만 사용합니다. 그룹 번호 없이 연결하기',
        noGroupOn: '그룹 번호 없이 연결합니다.',
        listLabel: '② 블록 연결',
        listHint: '아래 목록에서 사용할 블록을 선택하고 연결해 주세요.',
        connect: '연결하기',
        disconnect: '연결 끊기',
        connected: '연결됨',
        searching: '페어링 창에서 블록을 연결해 주세요.',
        notFound: '블록이 연결되지 않았습니다. 다시 연결해 주세요.',
        dup: other => `이미 '${other}'에 연결된 블록입니다. 다른 블록을 선택해 주세요.`,
        idle: '미연결 상태',
        done: '연결 완료',
        count: (a, b) => `${b}개 중 ${a}개 연결됨`
    },
    en: {
        title: 'Connect blocks',
        groupLabel: '① Group number',
        groupHint: 'Enter the group number on your blocks. It prevents Bluetooth mix-ups nearby so only your blocks connect.',
        groupNeed: 'Enter the group number first.',
        groupStale: n => `This number (${n}) was used before. Check that it matches the blocks you are using now.`,
        groupYes: 'Correct',
        groupSaved: 'Saved',
        groupPh: 'e.g. 12',
        noGroup: 'Using one set only. Connect without a group number',
        noGroupOn: 'Connecting without a group number.',
        listLabel: '② Connect blocks',
        listHint: 'Select the blocks you use from the list below and connect them.',
        connect: 'Connect',
        disconnect: 'Disconnect',
        connected: 'Connected',
        searching: 'Connect your block in the pairing window.',
        notFound: 'The block was not connected. Please connect again.',
        dup: other => `This block is already connected as '${other}'. Select another block.`,
        idle: 'Not connected',
        done: 'Done',
        count: (a, b) => `${a} of ${b} connected`
    }
};const PHOTO = {
    cubroiddcmotor01: motorPhoto, cubroiddcmotor02: motorPhoto, cubroidled: ledPhoto, cubroidmaster: masterPhoto,
    cubroidsound: soundPhoto, cubroidproximity: proximityPhoto, cubroidlighttouch: lighttouchPhoto
};
// 화면에는 앞의 0 없이(블록에 '83'처럼 붙어 있음, 2026-10-06 직원 의견). 저장·크롬 찾기 이름은 그대로 0083
const shown = g => String(g || '').replace(/^0+(?=\d)/, '');
// 모터는 사진이 같아서 번호를 붙인다
const BADGE = {cubroiddcmotor01: '1', cubroiddcmotor02: '2'};
// 경고 문장용 이름(카드 이름은 번역된 FormattedMessage 를 그대로 쓴다)
const NAME = {
    cubroiddcmotor01: ['DC 모터 1번', 'DC motor 1'], cubroiddcmotor02: ['DC 모터 2번', 'DC motor 2'], cubroidled: ['LED', 'LED'],
    cubroidmaster: ['마스터', 'Master'], cubroidsound: ['사운드', 'Sound'], cubroidproximity: ['근접 센서', 'Proximity sensor'],
    cubroidlighttouch: ['라이트 터치 센서', 'Light & touch sensor']
};

const S = {
    // 2026-10-12 직원 의견(한솔비 "페이지 축소해야만 아래까지 보여요"): 노트북처럼 화면이 낮으면 목록 아래가 잘렸다.
    //   창 높이는 robot-connect.css(.body, 화면 높이 따라 다름)로 묶고, 가운데(번호·목록)만 스크롤, [연결 완료]는 늘 보이게.
    //   되돌리기: body 의 display·flexDirection 과 scroll, robot-connect.css 를 빼면 예전처럼 한 덩어리.
    body: {padding: '1rem 1.25rem 1.1rem', background: '#fff', color: '#2b3445', fontSize: '0.9rem', lineHeight: 1.5,
        display: 'flex', flexDirection: 'column'},
    scroll: {flex: '1 1 auto', minHeight: 0, overflowY: 'auto', margin: '0 -0.6rem', padding: '0 0.6rem'},
    label: {fontWeight: 700, color: '#1f3b73', marginBottom: '0.35rem'},
    groupRow: {display: 'flex', alignItems: 'center', gap: '0.5rem'},
    input: {width: '7.5rem', fontSize: '1.4rem', fontWeight: 700, letterSpacing: '0.15em', textAlign: 'center',
        padding: '0.3rem 0.4rem', border: '2px solid #c9d5e8', borderRadius: '10px', outline: 'none', color: '#1f2633'},
    hint: {color: '#6b7689', fontSize: '0.8rem', marginTop: '0.35rem'},
    warn: {marginTop: '0.5rem', padding: '0.5rem 0.65rem', borderRadius: '8px', background: '#fff1ee', color: '#b4441c', fontSize: '0.82rem'},
    stale: {marginTop: '0.5rem', padding: '0.5rem 0.65rem', borderRadius: '8px', background: '#fff8e1', color: '#7a5a00', fontSize: '0.82rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem'},
    link: {border: 'none', background: 'none', color: '#4C97FF', cursor: 'pointer', fontSize: '0.78rem', padding: '0.3rem 0', textDecoration: 'underline'},
    section: {marginTop: '1rem', paddingTop: '0.8rem', borderTop: '1px solid #edf1f7'},
    row: {display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.4rem 0.45rem', borderRadius: '10px'},
    icon: {width: '2.6rem', height: '2.6rem', objectFit: 'contain', display: 'block'},
    badge: {position: 'absolute', right: '-0.15rem', top: '-0.15rem', width: '1.05rem', height: '1.05rem', borderRadius: '50%',
        background: '#1f3b73', color: '#fff', fontSize: '0.68rem', fontWeight: 800, lineHeight: '1.05rem', textAlign: 'center'},
    name: {fontWeight: 700, fontSize: '0.88rem'},
    status: {fontSize: '0.75rem', color: '#6b7689'},
    btn: {marginLeft: 'auto', flex: 'none', border: 'none', borderRadius: '8px', padding: '0.4rem 0.8rem', cursor: 'pointer',
        background: '#4C97FF', color: '#fff', fontWeight: 700, fontSize: '0.82rem'},
    btnGhost: {marginLeft: 'auto', flex: 'none', border: '1px solid #c9d5e8', borderRadius: '8px', padding: '0.3rem 0.6rem', cursor: 'pointer',
        background: '#fff', color: '#4a5568', fontSize: '0.75rem'},
    small: {border: '1px solid #b8cdef', background: '#fff', color: '#1f3b73', borderRadius: '8px', padding: '0.2rem 0.6rem', cursor: 'pointer',
        fontWeight: 700, fontSize: '0.78rem', flex: 'none'},
    foot: {flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginTop: '0.6rem',
        paddingTop: '0.7rem', borderTop: '1px solid #edf1f7'},
    done: {border: 'none', borderRadius: '10px', padding: '0.55rem 1.4rem', cursor: 'pointer', background: '#0FBD8C', color: '#fff',
        fontWeight: 800, fontSize: '0.9rem', flex: 'none'}
};

// '찾는 중'으로 기다리는 최대 시간(그 뒤로도 연결이 안 되면 '다시 연결해 주세요')
const SEARCH_LIMIT_MS = 40000;

class RobotConnect extends React.Component {
    constructor (props) {
        super(props);
        this.searchTimers = {};
        bindAll(this, ['applyGroup', 'handleGroupInput', 'handleGroupBlur', 'handleGroupKey', 'handleGroupYes', 'handleNoGroup', 'handleConnect',
            'handleDisconnect', 'handleConnected', 'handleRefresh', 'handleRequestError', 'handleDuplicate', 'setInput', 'setFocusRow']);
        const g = getGroup();
        this.state = {
            group: g,
            saved: false,
            // 이번 탭에서 아직 확인 안 한 옛 번호 → 연결 전에 "맞나요?"
            stale: Boolean(g) && !groupChecked(),
            // 이번 탭에서 "그룹 번호 없이"를 골랐으면 다시 열어도 그대로(2026-10-16 직원 의견: 다시 열면 또 입력해야 함)
            noGroup: !g && noGroupChosen(),
            needGroup: false,
            status: {}, // id → 'searching' | 'error' | 'dup:<id>'
            tick: 0
        };
    }
    componentDidMount () {
        const vm = this.props.vm;
        vm.on('PERIPHERAL_CONNECTED', this.handleConnected);
        vm.on('PERIPHERAL_DISCONNECTED', this.handleRefresh);
        vm.on('PERIPHERAL_CONNECTION_LOST_ERROR', this.handleRefresh);
        vm.on('PERIPHERAL_REQUEST_ERROR', this.handleRequestError);
        vm.on('PERIPHERAL_DUPLICATE_CONNECT', this.handleDuplicate);
        vm.on('EXTENSION_ADDED', this.handleRefresh);
        // 자동 재연결(끊겼다 다시 붙음)도 보이게 가끔 다시 본다
        this.timer = setInterval(this.handleRefresh, 1500);
        window.addEventListener(GROUP_EVENT, this.handleRefresh);
        if (!this.state.group && this.input) this.input.focus();
        else if (this.focusRow && this.focusRow.scrollIntoView) this.focusRow.scrollIntoView({block: 'nearest'});
    }
    componentWillUnmount () {
        clearTimeout(this.blurTimer);
        Object.keys(this.searchTimers).forEach(k => clearTimeout(this.searchTimers[k]));
        const vm = this.props.vm;
        vm.removeListener('PERIPHERAL_CONNECTED', this.handleConnected);
        vm.removeListener('PERIPHERAL_DISCONNECTED', this.handleRefresh);
        vm.removeListener('PERIPHERAL_CONNECTION_LOST_ERROR', this.handleRefresh);
        vm.removeListener('PERIPHERAL_REQUEST_ERROR', this.handleRequestError);
        vm.removeListener('PERIPHERAL_DUPLICATE_CONNECT', this.handleDuplicate);
        vm.removeListener('EXTENSION_ADDED', this.handleRefresh);
        clearInterval(this.timer);
        window.removeEventListener(GROUP_EVENT, this.handleRefresh);
    }
    ui () {
        return UI[/^ko/.test(this.props.intl.locale || '') ? 'ko' : 'en'];
    }
    // 그룹 번호가 준비됐나(있고 이번 수업에서 확인함, 또는 '번호 없이'를 고름)
    groupReady () {
        return this.state.noGroup || (Boolean(this.state.group) && !this.state.stale);
    }
    handleGroupInput (e) {
        const v = String(e.target.value || '').slice(0, 4);
        this.setState({group: v, saved: false, needGroup: false});
    }
    // 번호 저장은 바로, 화면 바꾸기(안내 접기)는 blur 때만 잠깐 뒤에 — 입력칸에서 바로 [연결하기]를 누르면
    // 안내가 먼저 사라져 목록이 위로 올라가 클릭이 빗나간다(10-01 실측). 클릭이 끝난 뒤 접으면 빈 칸을 남길 필요가 없다
    applyGroup (later) {
        // 0·00 은 '그룹 번호 없는 블록' — 빈 칸으로 지우고 경고를 띄우던 것을 "번호 없이 연결"로 알아듣는다(2026-10-16)
        const zero = isZeroGroup(this.state.group);
        const g = setGroup(this.state.group);
        if (g) markGroupChecked();
        if (g || zero) setNoGroupChosen(zero);
        const next = s => ({group: g, saved: Boolean(g), stale: false, noGroup: g ? false : (zero || s.noGroup), needGroup: zero ? false : s.needGroup});
        clearTimeout(this.blurTimer);
        if (later) this.blurTimer = setTimeout(() => this.setState(next), 300);
        else this.setState(next);
    }
    handleGroupBlur () {
        this.applyGroup(true);
    }
    handleGroupKey (e) {
        if (e.key === 'Enter') { e.preventDefault(); this.applyGroup(false); }
    }
    handleGroupYes () {
        markGroupChecked();
        this.setState({stale: false, needGroup: false});
    }
    handleNoGroup () {
        setGroup('');
        setNoGroupChosen(true);
        this.setState({group: '', noGroup: true, stale: false, needGroup: false});
    }
    setInput (el) {
        this.input = el;
    }
    setFocusRow (el) {
        this.focusRow = el;
    }
    handleConnect (e) {
        const id = e.currentTarget.getAttribute('data-id');
        // 입력칸에서 바로 [연결]을 누르면 blur 보다 먼저 올 수 있다 → 지금 칸의 값을 저장부터
        const typed = normGroup(this.state.group);
        const zero = isZeroGroup(this.state.group); // 0·00 을 넣고 바로 [연결하기]를 누른 경우(blur 보다 먼저 올 수 있음)
        if (typed !== getGroup()) {
            setGroup(typed);
            if (typed) markGroupChecked();
        }
        if (typed || zero) setNoGroupChosen(zero);
        if (zero && !this.state.noGroup) this.setState({group: '', noGroup: true, needGroup: false});
        // 번호를 고쳐 쓰면 입력칸 blur 에서 stale 이 풀린다. 그대로 둔 옛 번호면 "맞나요?"에 먼저 답해야 한다
        // 화면 접기가 미뤄져 있어도(blur 직후) 이번 탭에서 번호를 확인했으면 통과
        const ready = this.state.noGroup || zero || (Boolean(typed) && !(this.state.stale && !groupChecked()));
        if (!ready) {
            this.setState({needGroup: true, group: typed});
            if (this.input) this.input.focus();
            return;
        }
        markGroupChecked();
        const vm = this.props.vm;
        const go = () => {
            this.setState(s => ({group: typed, stale: false, status: Object.assign({}, s.status, {[id]: 'searching'})}));
            // 안전망: 고른 뒤 연결(GATT)이 소리 없이 실패하거나 알림이 안 오면 '찾는 중'에서 영영 멈춘다(2026-10-06 직원 실측)
            // → 40초 지나도 연결 안 됐으면 실패로 보여 준다(페어링 창에서 오래 고르는 시간을 넉넉히 둠)
            clearTimeout(this.searchTimers[id]);
            this.searchTimers[id] = setTimeout(() => {
                this.setState(s => (s.status[id] === 'searching' && !this.props.vm.getPeripheralIsConnected(id) ?
                    {status: Object.assign({}, s.status, {[id]: 'error'})} : null));
            }, SEARCH_LIMIT_MS);
            // 이 클릭 안에서 바로 찾기 시작 — 크롬 페어링 창은 사람 클릭이 있어야 뜬다
            vm.scanForPeripheral(id);
        };
        if (vm.extensionManager.isExtensionLoaded(id)) go();
        else vm.extensionManager.loadExtensionURL(id).then(go);
    }
    handleDisconnect (e) {
        const id = e.currentTarget.getAttribute('data-id');
        this.props.vm.disconnectPeripheral(id);
        this.setState(s => ({status: Object.assign({}, s.status, {[id]: null})}));
    }
    handleConnected () {
        // 어느 모듈인지 이벤트에 없다 → 연결된 것만 '찾는 중' 표시를 지운다
        const vm = this.props.vm;
        const status = Object.assign({}, this.state.status);
        ROBOT_IDS.forEach(id => { if (vm.getPeripheralIsConnected(id)) status[id] = null; });
        this.setState({status});
    }
    handleRequestError (data) {
        // VM 이 이 알림을 모듈 이름 없이 넘긴다(scratch-vm virtual-machine.js) → 지금 '찾는 중'인 모듈을 실패로(한 번에 하나만 찾는다)
        const id = data && data.extensionId;
        this.setState(s => {
            const status = Object.assign({}, s.status);
            if (id && ROBOT_IDS.indexOf(id) >= 0) status[id] = 'error';
            else Object.keys(status).forEach(k => { if (status[k] === 'searching') status[k] = 'error'; });
            return {status};
        });
    }
    handleDuplicate (data) {
        const id = data && data.extensionId;
        if (!id) return;
        this.setState(s => ({status: Object.assign({}, s.status, {[id]: `dup:${data.conflictExtensionId || ''}`})}));
    }
    handleRefresh () {
        this.setState(s => ({tick: s.tick + 1}));
    }
    renderRow (id) {
        const L = this.ui();
        const vm = this.props.vm;
        const ext = extensionData.find(x => x.extensionId === id) || {};
        const loaded = vm.extensionManager.isExtensionLoaded(id);
        const on = loaded && vm.getPeripheralIsConnected(id);
        const st = this.state.status[id];
        const focus = this.props.focusId === id;
        let statusText = L.idle;
        let statusColor = '#6b7689';
        if (on) { statusText = `● ${L.connected}`; statusColor = '#0b8f6a'; } else if (st === 'searching') { statusText = L.searching; statusColor = '#4C97FF'; } else if (st === 'error') {
            statusText = L.notFound; statusColor = '#b4441c';
        } else if (st && st.indexOf('dup:') === 0) {
            const o = NAME[st.slice(4)];
            statusText = L.dup(o ? o[/^ko/.test(this.props.intl.locale || '') ? 0 : 1] : st.slice(4));
            statusColor = '#b4441c';
        }
        return (
            <div
                key={id}
                ref={focus ? this.setFocusRow : null}
                style={Object.assign({}, S.row, focus ? {background: '#eaf2ff'} : {}, on ? {background: '#e8f8f2'} : {})}
            >
                <div style={{position: 'relative', flex: 'none'}}>
                    <img
                        alt=""
                        src={PHOTO[id] || ext.connectionSmallIconURL || ext.insetIconURL}
                        style={S.icon}
                    />
                    {BADGE[id] ? <span style={S.badge}>{BADGE[id]}</span> : null}
                </div>
                <div style={{minWidth: 0}}>
                    <div style={S.name}>{ext.name || id}</div>
                    <div style={Object.assign({}, S.status, {color: statusColor})}>{statusText}</div>
                </div>
                {on ? (
                    <button
                        data-id={id}
                        style={S.btnGhost}
                        onClick={this.handleDisconnect}
                    >{L.disconnect}</button>
                ) : (
                    <button
                        data-id={id}
                        style={Object.assign({}, S.btn, this.groupReady() ? {} : {background: '#a9c8f5'})}
                        onClick={this.handleConnect}
                    >{L.connect}</button>
                )}
            </div>
        );
    }
    render () {
        const L = this.ui();
        const vm = this.props.vm;
        const loadedIds = ROBOT_IDS.filter(id => vm.extensionManager.isExtensionLoaded(id));
        const onCount = loadedIds.filter(id => vm.getPeripheralIsConnected(id)).length;
        // 순서는 늘 같게 — 넣을 때마다 줄이 움직이면 학생이 옆 줄을 누른다(2026-10-01 실측)
        const order = ROBOT_IDS;
        return (
            <Modal
                className={`${styles.modalContent} ${rcStyles.panel}`}
                contentLabel={L.title}
                id="curoaiRobotConnect"
                onRequestClose={this.props.onClose}
            >
                <div
                    className={rcStyles.body}
                    style={S.body}
                >
                    <div style={S.scroll}>
                    <div style={S.label}>{L.groupLabel}</div>
                    <div style={S.groupRow}>
                        <input
                            aria-label={L.groupLabel}
                            inputMode="numeric"
                            placeholder={L.groupPh}
                            ref={this.setInput}
                            style={Object.assign({}, S.input, this.state.needGroup ? {borderColor: '#ff8a7a'} : {})}
                            type="text"
                            value={shown(this.state.group)}
                            onBlur={this.handleGroupBlur}
                            onChange={this.handleGroupInput}
                            onKeyDown={this.handleGroupKey}
                        />
                        {this.state.saved ? <span style={{color: '#0b8f6a', fontSize: '0.8rem', fontWeight: 700}}>{`✓ ${L.groupSaved}`}</span> : null}
                    </div>
                    <div style={S.hint}>{L.groupHint}</div>
                    <div>
                    {this.state.stale && this.state.group ? (
                        <div style={S.stale}>
                            <span>{L.groupStale(shown(this.state.group))}</span>
                            <button
                                style={S.small}
                                onClick={this.handleGroupYes}
                            >{L.groupYes}</button>
                        </div>
                    ) : null}
                    {!this.state.group && !this.state.noGroup ? (
                        <div>
                            <div style={this.state.needGroup ? S.warn : Object.assign({}, S.warn, {background: '#fff7f5'})}>{L.groupNeed}</div>
                            <button
                                style={S.link}
                                onClick={this.handleNoGroup}
                            >{L.noGroup}</button>
                        </div>
                    ) : null}
                    {this.state.noGroup ? <div style={Object.assign({}, S.hint, {color: '#b4441c'})}>{L.noGroupOn}</div> : null}
                    </div>
                    <div style={S.section}>
                        <div style={Object.assign({}, S.label, {display: 'flex', justifyContent: 'space-between'})}>
                            <span>{L.listLabel}</span>
                            {loadedIds.length ? <span style={{fontWeight: 400, fontSize: '0.78rem', color: '#6b7689'}}>{L.count(onCount, loadedIds.length)}</span> : null}
                        </div>
                        <div style={Object.assign({}, S.hint, {marginTop: 0, marginBottom: '0.3rem'})}>{L.listHint}</div>
                        {order.map(id => this.renderRow(id))}
                    </div>
                    </div>
                    <div style={S.foot}>
                        <button
                            style={S.done}
                            onClick={this.props.onClose}
                        >{L.done}</button>
                    </div>
                </div>
            </Modal>
        );
    }
}

RobotConnect.propTypes = {
    focusId: PropTypes.string,
    intl: intlShape.isRequired,
    onClose: PropTypes.func.isRequired,
    vm: PropTypes.instanceOf(VM).isRequired
};

export default injectIntl(RobotConnect);
